import React, { useState, useEffect } from 'react';
import { useDependencies } from '@servicetitan/react-ioc';
import { ServiceFinanceStore } from '../../../stores/service-finance.store';
import * as Styles from './esign-agreement-page.less';

export const ESignAgreementPage: React.FC = () => {
    const [{ eSignConsentAgreement }] = useDependencies(ServiceFinanceStore);
    const [referenceNode, setReferenceNode] = useState<HTMLDivElement | undefined>();
    useEffect(() => {
        return () => {
            try {
                if (referenceNode === undefined) {
                    return;
                }
                referenceNode.removeEventListener('scroll', handleScroll);
            } catch (error) {
                console.log(`Error in ESignAgreementPage ${error}`);
            }
        };
    }, []);

    function handleScroll(event: any) {
        const node = event.target;
        // No need to scroll to exact bottom. Give it 200 px buffer.
        const bottom = node.scrollHeight - node.scrollTop - 200 <= node.clientHeight;
        if (bottom) {
            eSignConsentAgreement.scrolledToBottom = true;
        }
    }

    const paneDidMount = (node: HTMLDivElement) => {
        if (node) {
            node.addEventListener('scroll', handleScroll);
            setReferenceNode(node);
        }
    };

    return (
        <div className={Styles.container} ref={paneDidMount}>
            <div className={Styles.header}>
                Welcome to a Safe, Fast, Friendly, “Green” Paper-Free Transaction.
                <br />
                Federal ESign Disclosure and Consent
            </div>
            <br />
            <div className={Styles.content}>
                Please read this Federal ESIGN Disclosure and Consent carefully and print
                and/download or otherwise keep a copy for your records.
            </div>
            <div className={Styles.content}>
                The following disclosures are required to be provided to you by your dealer (or the
                dealer’s service provider, Service Finance Company, LLC) under the federal
                Electronic Signatures in Global and National Commerce Act (“ESIGN”). They are
                necessary in order for you to provide your consent to receive and sign the Retail
                Installment Sale Contract or Promissory Note (depending on your state of residence),
                any Addendum or Amendment thereto (collectively, the “Retail Installment Sale
                Contract”), the Consumer Loan Application and related disclosures as further
                described below electronically.
            </div>
            <div className={Styles.content}>
                <div className={Styles.subTitle}>
                    Electronic Delivery of Disclosures and Notices
                </div>
                By clicking in the box below, you are consenting to receive the Retail Installment
                Sale Contract, the Consumer Loan Application and all disclosures, notices, terms and
                conditions, and other related documents and any changes to the foregoing
                (collectively, the “Contract Documents”) electronically. Your consent will apply to
                the dealer, Service Finance Company, LLC (the “Company”), and to any subsequent
                assignee(s) of the Contract Documents. In order to access, receive and retain the
                Contract Documents, you must satisfy the minimum system requirements outlined below.
                We reserve the right, in our sole discretion, to provide you with any of the
                Contract Documents in paper form instead of, or in addition to, electronic form.
            </div>
            <div className={Styles.content}>
                <div className={Styles.subTitle}>Paper Delivery of Disclosures and Notices</div>
                You are not required to provide your consent in order to proceed with the Retail
                Installment Sale Contract. If you prefer not to provide your consent, all of the
                Contract Documents will be executed and provided to you in paper form. If you decide
                to provide your consent, you still have the right to receive a paper copy of the
                Contract Documents. To receive a paper copy at no charge, please request it in one
                of the following ways: send an e-mail message with your name and mailing address to{' '}
                <a href="mailto:info@svcfin.com">info@svcfin.com</a>; call us at (866) 254-0497; fax
                us at (866) 257-8122; or write to us at Service Finance Company, LLC at 555 South
                Federal Highway, Suite 200, Boca Raton, Florida 33432 with your name and mailing
                address. Be sure to state that you are requesting a copy of the Contract Documents.
            </div>
            <div className={Styles.content}>
                <div className={Styles.subTitle}>System Requirements to Access Information</div>
                To receive an electronic copy of the Contract Documents, you must have the following
                equipment and software:
                <ul>
                    <li>
                        A personal computer or other device which is capable of accessing the
                        Internet. Your access to this page verifies that your system/device meets
                        these requirements.
                    </li>
                    <li>
                        An Internet web browser which is capable of supporting 128-bit SSL encrypted
                        communications, which requires a minimum web browser version of either
                        Microsoft® Internet Explorer version 6.0 (available for downloading at{' '}
                        <a
                            href="http://www.microsoft.com/windows/ie/downloads/default.asp"
                            target="_blank"
                        >
                            {' '}
                            http://www.microsoft.com/windows/ie/downloads/default.asp
                        </a>
                        ) and your system or device Rev 010317 must have 128-bit SSL encryption
                        software. Your access to this page
                    </li>
                    <li>
                        You must have software which permits you to receive and access Portable
                        Document Format or “PDF” files, such as Adobe® Acrobat Reader® version 8.0
                        and above (download at{' '}
                        <a
                            href="http://www.adobe.com/products/acrobat/readstep2.html"
                            target="_blank"
                        >
                            http://www.adobe.com/products/acrobat/readstep2.html
                        </a>
                        ). These ESIGN disclosures and the Contract Documents are both provided to
                        you in PDF; therefore, your access to this page from your own system/device
                        verifies that your system/device has the necessary software to permit you to
                        receive and access PDF files.
                    </li>
                    <li>
                        A valid email address or, if you do not have a valid email address, you can
                        use your valid user credentials to access a secure webpage at{' '}
                        <a href="https://esign.svcfin.com" target="_blank">
                            https://esign.svcfin.com
                        </a>
                    </li>
                </ul>
            </div>
            <div className={Styles.content}>
                <div className={Styles.subTitle}>System Requirements to Retain Information</div>
                If you have a valid email address, a copy of the Contract Documents will be provided
                to you upon execution by email in the form of a PDF attachment. A copy of the
                Contract Documents also will be made available to you upon execution by your
                accessing a secure webpage at
                https://esign.svcfin.com/(S(2gec11qz5gd5134cpjq2gpau))/Home/login and logging in
                using your individual access credentials. To retain a copy of the Contract
                Documents, you may choose to print them, save them or both. In order to print them,
                you must have a functioning printer connected to your personal computing device. If
                you wish to retain an electronic version of the Contract Documents, you must have a
                personal computing device with the hardware and software necessary to download and
                store PDF files.
            </div>
            <div className={Styles.content}>
                <div className={Styles.subTitle}>
                    Withdrawal of Electronic Acceptance of Disclosures and Notices
                </div>
                You can also contact us in any of the ways described above to withdraw your consent
                to receive any future disclosures, notices, terms and conditions, and other
                documents related to the Contract Documents electronically, if any. There are no
                fees for withdrawal of your consent. If you withdraw your consent, we will send all
                future Contract Documents (if any) to you in paper form. If you decide to withdraw
                your consent, the withdrawal will not affect the legal effectiveness, validity, and
                enforceability of prior electronic Contract Documents. Your withdrawal of your
                consent will be effective within a reasonable period of time following our receipt
                thereof to allow for time to process your request.
            </div>
            <div className={Styles.content}>
                <div className={Styles.subTitle}>Changing your Email Address</div>
                It is important that you keep your email address current with us. Therefore, please
                notify us immediately at the addresses and/or telephone numbers set forth above if
                you change your email address. We may treat your failure to provide us with a valid
                email address, or the malfunction of the email address we have for you, as a
                withdrawal of your consent to receive Contract Documents electronically.
            </div>
            <div className={Styles.content}>
                <div className={Styles.subTitle}>
                    Right to Discontinue Electronic Provision of Contract Documents
                </div>
                We reserve the right, in our sole discretion, to discontinue electronic provision of
                Contract Documents. We will provide you with notice of any such termination as
                required by applicable law. If we discontinue electronic provision of Contract
                Documents, we will send all future Contract Documents (if any) to you in paper form.
            </div>
            <div className={Styles.content}>
                <div className={Styles.subTitle}>Systems Enhancements.</div>
                If the system requirements for accessing the copy of your Contract Documents on the
                secure webpage change so as to require different or additional hardware or software,
                or upgrades, plugins or enhancements to the hardware or software used by you (all
                such changes referred to as a “Systems Enhancement”), we will notify you and you may
                either withdraw your consent or upgrade your hardware and/or software as required by
                the Systems Enhancement. If we determine that the need for a Systems Enhancement
                creates a material risk that you may not be able to access or retain the Contract
                Documents electronically, we will notify you and allow you to either: (a) withdraw
                your consent or (b) re-confirm your consent to receive the Contract Documents in a
                manner that reasonably demonstrates that you have upgraded your hardware or software
                to conform to the required Systems Enhancement. If you choose to withdraw your
                consent, or you fail to re-confirm your consent (which we will treat as your
                withdrawal of consent), you will no longer receive Contract Documents electronically
                and we will send all future Contract Documents to you in paper form.
                <br />
                <br />
                By clicking the box <strong>“Yes, I consent”</strong> below, you:
                <ul>
                    <li>
                        acknowledge your receipt and review of the foregoing Federal ESIGN
                        Disclosure and Consent;
                    </li>
                    <li>
                        give your affirmative consent to our providing electronic Contract Documents
                        to you as described above;
                    </li>
                    <li>
                        acknowledge that you are signing the Contract Documents using an electronic
                        signature and thus agreeing to their terms;
                    </li>
                    <li>
                        agree that the electronic Contract Documents may be authenticated, stored
                        and transmitted (as applicable) by electronic means and are valid for all
                        legal purposes per applicable law;
                    </li>
                    <li>
                        agree that the Company and any person to whom the electronic Contract
                        Documents are later transferred shall have the right to convert them at any
                        time to paper-based Contract Documents, and in such event, the paper-based
                        Contract Documents will be enforceable and valid, and that you intend that
                        the printing of the representation of your electronic signature on the
                        paper-based Contract Documents from the system in which the electronic
                        Contract Documents are stored will be your original signature on the
                        paper-based Contract Documents and will serve to indicate your present
                        intention to authenticate the paper-based Contract Documents, and that the
                        paper-based Contract Documents will then be a valid original writing for all
                        legal purposes, and that upon conversion to paper-based Contract Documents,
                        your obligations in the electronic Contract Documents shall automatically
                        transfer to and be contained in the paper-based Contract Documents and you
                        intend to be bound by such obligations;
                    </li>
                    <li>
                        confirm that your computer or access device satisfies the hardware and
                        software requirements needed to access and retain the Contract Documents
                        that will be Rev 010317 provided to you in electronic form, including
                        through use of a valid email address to access the Contract Documents as an
                        attachment in portable document file (PDF) format and/or by logging in to
                        our secure webpage to access the Contract Documents in PDF format.
                    </li>
                </ul>
                If you click the box <strong>“No, I do not consent”</strong>, you are not consenting
                to our providing electronic Contract Documents to you as described above, and you
                will not be permitted to proceed to the Contract Documents via this method and
                instead must contact us as described above for further information.
            </div>
        </div>
    );
};
